import api from './api';

export interface AssetListItem {
  key: string;
  name: string;
  size: number;
  lastModified: string;
  type: 'file' | 'folder';
  storageClass?: string;
}

export interface AssetBrowseResult {
  prefix: string;
  folders: AssetListItem[];
  files: AssetListItem[];
  truncated: boolean;
  continuationToken?: string;
}

export interface AssetFileListResult {
  prefix: string;
  files: AssetListItem[];
  truncated: boolean;
  continuationToken?: string;
}

export interface AssetMetadata {
  key: string;
  size: number;
  lastModified: string;
  contentType?: string;
  storageClass?: string;
  metadata?: Record<string, string>;
  etag?: string;
}

export interface OCIMetadata {
  source?: string;
  registryName?: string;
  registryUrl?: string;
  repository?: string;
  tag?: string;
  platform?: string;
  sha256?: string;
  sidecarSha256?: string;
  pulledAt?: string;
  imageRef?: string;
}

export const assetsApi = {
  browse: async (bucketId: string, prefix: string = '', continuationToken?: string): Promise<AssetBrowseResult> => {
    const params: Record<string, string> = { bucketId, prefix };
    if (continuationToken) params.continuationToken = continuationToken;
    const { data } = await api.get<AssetBrowseResult>('/assets/browse', { params });
    return data;
  },

  listFiles: async (bucketId: string, prefix: string = '', recursive: boolean = false, continuationToken?: string): Promise<AssetFileListResult> => {
    const params: Record<string, string> = { bucketId, prefix, recursive: String(recursive) };
    if (continuationToken) params.continuationToken = continuationToken;
    const { data } = await api.get<AssetFileListResult>('/assets/files', { params });
    return data;
  },

  deleteFiles: async (bucketId: string, keys: string[]): Promise<{ success: boolean; deleted: string[]; errors: Array<{ key: string; error: string }> }> => {
    const { data } = await api.delete('/assets/files', { params: { bucketId }, data: { keys } });
    return data;
  },

  getMetadata: async (bucketId: string, key: string): Promise<AssetMetadata> => {
    const { data } = await api.get<AssetMetadata>('/assets/metadata', { params: { bucketId, key } });
    return data;
  },

  downloadFile: (bucketId: string, key: string): void => {
    const baseURL = (import.meta as any).env?.VITE_API_URL || '/api';
    const url = `${baseURL}/assets/download?bucketId=${encodeURIComponent(bucketId)}&key=${encodeURIComponent(key)}`;
    window.open(url, '_blank');
  },

  getOCIMetadata: async (bucketId: string, keys: string[]): Promise<Record<string, OCIMetadata>> => {
    const { data } = await api.post<Record<string, OCIMetadata>>('/assets/oci-metadata', { keys }, { params: { bucketId } });
    return data;
  },

  verifyChecksum: async (bucketId: string, key: string): Promise<{
    key: string;
    actual: string;
    metaHash: string | null;
    sidecarHash: string | null;
    metaMatch: boolean | null;
    sidecarMatch: boolean | null;
    sidecarConsistent: boolean;
    match: boolean;
  }> => {
    const { data } = await api.post('/assets/verify-checksum', { key }, { params: { bucketId } });
    return data;
  },

  uploadFile: (
    bucketId: string,
    key: string,
    file: File,
    onProgress?: (loaded: number, total: number) => void,
  ): { promise: Promise<void>; abort: () => void } => {
    const xhr = new XMLHttpRequest();
    const baseURL = (import.meta as any).env?.VITE_API_URL || '/api';

    const promise = new Promise<void>((resolve, reject) => {
      xhr.open('POST', `${baseURL}/assets/upload?bucketId=${encodeURIComponent(bucketId)}`);

      const token = (window as any).__keycloakToken__;
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(event.loaded, event.total);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          try {
            const errData = JSON.parse(xhr.responseText);
            reject(new Error(errData.error || `Upload failed with status ${xhr.status}`));
          } catch {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.onabort = () => reject(new Error('Upload cancelled'));

      const formData = new FormData();
      formData.append('key', key);
      formData.append('file', file);

      xhr.send(formData);
    });

    return {
      promise,
      abort: () => xhr.abort(),
    };
  },
};
